/* eslint-disable max-lines */
import { comparer } from 'mobx';
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';

import { AdditionalMeta, PageData, PaginationAttribute } from 'bernie-http';
import { codeSplit, Controller, FetchOptions, FetchPageOptions, merge, SerializedData } from 'bernie-core';
import { Context } from 'bernie-context';
import { formatText, Localization } from 'bernie-l10n';
import { LocationReactorResult } from 'bernie-client';

import { evaluateAndLogExperiment, hasStoresChanged } from 'stores/utils';
import { getPackageType, getSearchTimeout, shouldCallMultiItemSessionMutation } from 'stores/multi-item-store-util';
import { MultiItemStore } from 'stores/multi-item-store';
import { PageLayoutStore } from 'src/common/stores/page-layout/page-layout-store';
import { SearchCriteriaModel, singleUseQueryParams, untrackedQueryParams } from 'stores/models';
import { SearchCriteriaUtils } from 'stores/models/search-criteria-utils';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchResultsStore } from 'stores/search-results-store';

import { PackageType } from 'models/multi-item/multi-item-types';
import { SEARCH_PAGE_EG_CLICKSTREAM_NAME, SearchClickstreamPageNameDetailed, SearchPageName } from 'constants/pages';

import { getTrackingInfoForLogging } from 'utils/logging-utils';
import { meta } from 'utils/meta';
import { processClientSideDefaultRequestFields } from './shared-controller-modules/utils';

import { CONTROLLER_EVENTS } from 'controllers/events/controller-events';
import { convertHashParams, shouldHashOverrideDefaultValues, shouldSearchHashParams } from './converters/legacy-hash-params';
import { v4 as uuid } from 'uuid';

const { withoutKeys, withKeys } = merge;

const CHINA_POS_SITE_ID = 100001;

export class SearchController implements Controller {
  public bundles = ['search'];
  // @ts-ignore
  public component = codeSplit(() => import(/* webpackChunkName: "search" */ 'views/hotel/search.tsx') as any);
  public pageId = `${SearchPageName.SA},H,20`;
  public path = '/:locale([a-z]{2}-[a-z]{2})?/(search|Hotel-Search|search.do)*';
  public exact = true;
  public routeName = 'search';
  public routeData = { rfrrNamespace: 'HOT.SR', pageName: 'page.Hotel-Search' };

  // eslint-disable-next-line complexity
  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('SearchController.fetch receieved an invalid StoreContainer');
    }

    const { isServer, query, stores, logger } = options;
    const fetchStartTime = Date.now();
    const context: ContextStore = stores.get<ContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const searchCriteria: SearchCriteriaModel = stores.get<SearchCriteriaModel>('searchCriteria');
    const searchResults: SearchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const experiments: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const pageLayoutStore: PageLayoutStore = stores.get<PageLayoutStore>('pageLayout');

    // sanitize incoming params
    const singleUseCriteria: Record<string, string | string[]> = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...Object.keys(singleUseCriteria), 'prevQuery', 'l10n'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery = withoutKeys(untrackedKeys).merge({}, query);
    const packageType = getPackageType(query);

    this.initializeMultiItem(multiItem, query, nextQuery, context);

    searchResults.pageData = page;

    const isMapSearch = options.query.pwaOverlay === 'map';

    const SHARED_UI_ACTIVE_BUCKET = evaluateAndLogExperiment('EG_Property_Search_Query_Migration', context, experiments, page);

    if (isMapSearch) {
      searchResults.initialLoadSearchPagination = undefined;
      searchResults.hotelsMaxDisplay = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
    } else {
      const isVRBO = context.site.brand === 'vrbo';
      const fetchCount = SearchResultsStore.INITIAL_LISTING_FETCH_COUNT;
      searchResults.initialLoadSearchPagination = isVRBO
        ? {}
        : {
            size: fetchCount,
            startingIndex: 0,
          };
    }

    if (searchResults.foregoClientFetch) {
      searchResults.foregoClientFetch = false;
      const criteria = searchResults.searchCriteria?.toJSON();
      const sqaParsedOut = SearchQueryAdapter.parseOut(criteria);

      return { query: sqaParsedOut, replace: true } as LocationReactorResult;
    }
    // if client side detects hash params, handle them
    if (!isServer && shouldSearchHashParams(nextQuery)) {
      const hashParams = convertHashParams();

      if (shouldHashOverrideDefaultValues(hashParams)) {
        searchResults.defaultRequestFields = {};
      }
      if (hashParams) {
        return { query: hashParams, replace: true } as LocationReactorResult;
      }
    }
    // server rendered with defaulted params, we need to adjust URLs first
    let newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const isMobileWeb = context.deviceInformation?.type === 'MOBILE_PHONE';
    const useBEXQuickFiltersTemplate = isMobileWeb
      ? experiments?.exposures?.Introduce_Quick_Sort_and_Filters_at_top_of_SRP_mweb?.bucket === 1
      : experiments?.exposures?.Introduce_Quick_Sort_and_Filters_at_top_of_SRP_web?.bucket === 1;

    pageLayoutStore.setSRPTemplate(context, multiItem.isPackageShoppingPath(), useBEXQuickFiltersTemplate);

    const forceFetch = searchResults.shouldFetch;
    // check if queries are different to previous state
    if (forceFetch || !comparer.structural(nextQuery, prevQuery)) {
      // queries are meaningfully diff, setup base stores again
      searchCriteria.setValues({
        ...SearchCriteriaModel.getDefaults(),
        ...SearchCriteriaUtils.getFilterDefaults(),
        ...nextQuery,
      });

      const modelData = SearchQueryAdapter.parseIn(searchCriteria.toJSON());
      const incomingSearchCriteria = new SearchCriteriaModel(modelData, new Localization(context.locale));

      // fetches are only carried out if criteria is also changed
      if (forceFetch || !searchResults.searchCriteria || hasStoresChanged(searchResults.searchCriteria, incomingSearchCriteria)) {
        if (isServer === true && query && multiItem.getPackageType() === PackageType.FH) {
          const bucket = evaluateAndLogExperiment('MIS_Lodging_SRP_Enable_Client_Side_Rendering_', context, experiments, page);
          query.skipServerFetch = bucket === 1 ? 'true' : query.skipServerFetch;
        }
        const skipSearchResultsFetch = isServer === true && query && query.skipServerFetch === 'true';

        const includePropertyListing = multiItem?.isPackageShoppingPath() || SHARED_UI_ACTIVE_BUCKET === 0;

        await this.doNetworking(
          page,
          analyticsStore,
          context,
          searchResults,
          incomingSearchCriteria,
          skipSearchResultsFetch,
          singleUseCriteria,
          searchCriteria,
          multiItem,
          packageType,
          experiments,
          includePropertyListing
        )
          .then(() => {
            if (logger) {
              logger.logEvent(
                CONTROLLER_EVENTS.SEARCH_CONTROLLER_FETCH,
                `fetchTime=${(Date.now() - fetchStartTime).toString()} ms`,
                `, isServer=${isServer}, ${getTrackingInfoForLogging(context)}`
              );
            }
          })
          .catch(error => {
            if (logger) {
              logger.logEvent(CONTROLLER_EVENTS.SEARCH_CONTROLLER_FETCH_FAILURE, error);
            }
          });
      }
    }

    // cleanup defaultRequestFields so they don't hang around for subsequent calls on clientside
    newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const destinationTitle = searchCriteria.destination;
    // `formatText` is deprecated. We should have the api create the localized page title and send it to the client.
    const pageTitle = destinationTitle
      ? formatText('hotels.searchResults.successfulSearch.pageTitle', destinationTitle)
      : formatText('hotels.searchResults.invalidSearch.pageTitle');
    page.title = pageTitle;
    // `document` is undefined on the server only set `document.title` on the client.
    if (!isServer) {
      document.title = pageTitle;
    }
  }

  private initializeMultiItem(multiItem: MultiItemStore, query: SerializedData, nextQuery: SerializedData, context: ContextStore) {
    multiItem.buildContext(query);
    multiItem.referrer = query.rfrr;

    if (query.multiProp === 'true' && !query.businessModelFilter && nextQuery) {
      const defaultBusinessModel = multiItem.getDefaultBusinessModel();
      if (multiItem.cartInfo && multiItem.cartInfo.identifier === 'mixAndMatch') {
        multiItem.removeMixAndMatchCart();
        multiItem.getOrCreateCart(context, defaultBusinessModel);
      }
      nextQuery.businessModelFilter = defaultBusinessModel;
    }
  }

  private async doNetworking(
    page: PageStore,
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    searchResults: SearchResultsStore,
    incomingSearchCriteria: SearchCriteriaModel,
    skipServerFetch: boolean,
    singleUseCriteria: SerializedData,
    searchCriteria: SearchCriteriaModel,
    multiItem: MultiItemStore,
    packageType: PackageType,
    experiments?: ExperimentStore,
    includePropertyListing?: boolean
  ) {
    const pageFetch = page.fetch(meta['hotel-search'], context.site, context.locale);
    const isMultiItem = multiItem?.isPackageShoppingPath();

    if (isMultiItem && searchResults.searchCriteria) {
      const isClientSide = !multiItem.isServerSide();
      const shouldCallCreateMISession = shouldCallMultiItemSessionMutation(
        searchResults.searchCriteria,
        incomingSearchCriteria,
        multiItem.getPackageType()
      );

      if (isClientSide && shouldCallCreateMISession) {
        await searchResults.createSessionBeforeMultiItemFetch(multiItem, context, incomingSearchCriteria);

        // Setting the regionId to null so that correct regionId is retrieved in spinner for multicity searches.
        if (searchResults.searchCriteria.regionId !== incomingSearchCriteria.regionId) {
          incomingSearchCriteria.regionId = undefined;
        }
      }
    }

    // Add an exception to allow for the filters to be updated if overfiltered
    // Add another expection to allow searching when the previous search was invalid
    const resultsHaveNoErrors = !searchResults.isError;
    const resultsOverFiltered = searchResults.overFiltered;
    const previousSearchWasInvalid = this.previousSearchWasInvalid(searchResults);

    if (resultsHaveNoErrors || resultsOverFiltered || previousSearchWasInvalid) {
      const includeDynamicMap = context.site.brand === 'vrbo';
      searchResults.updateSearchCriteria(incomingSearchCriteria);
      const searchResultsFetch =
        skipServerFetch || !includePropertyListing
          ? Promise.resolve()
          : searchResults.fetch(
              analyticsStore,
              context,
              getSearchTimeout(packageType),
              singleUseCriteria,
              multiItem,
              page,
              experiments,
              includeDynamicMap
            );

      await Promise.all([pageFetch, searchResultsFetch]);
      if (!includePropertyListing) {
        if (searchResults.searchCriteria) {
          searchResults.searchCriteria.searchId = uuid();
        }
        searchResults.updateIsLoading(false);
      }
      this.updateSearchCriteriaFromSearchResults(searchResults, searchCriteria);
    }
  }

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    const additionalMeta: AdditionalMeta[] = this.addResourceHints(options.context);

    const seo = {
      seo: {
        ...(options.context?.site?.id !== CHINA_POS_SITE_ID && { additionalMeta }),
        // Temporary fix for issue: https://jira.expedia.biz/browse/BHS-17025
        robots: 'noindex,nofollow',
      },
    };

    const packageType = getPackageType(options.query);
    const pageData: Partial<PageData> = packageType
      ? this.getPackagePageData(packageType)
      : {
          navigationId: 'hotel',
          clickstreamPageName: SEARCH_PAGE_EG_CLICKSTREAM_NAME,
          clickstreamPageNameDetailed: SearchClickstreamPageNameDetailed.SA,
          pageName: SearchPageName.SA,
        };

    return Promise.resolve({
      ...seo,
      ...pageData,
    }) as Promise<PageData>;
  }

  private addResourceHints(context: Context) {
    return [new PaginationAttribute(context?.deviceInformation?.mobile ? 'dns-prefetch' : 'preconnect', 'https://maps.googleapis.com/')];
  }

  private getPackagePageData(packageType: PackageType) {
    const pageName = SearchPageName[packageType];

    return {
      pageName,
      pageId: `${pageName},P,20`,
      navigationId: 'package',
      clickstreamPageName: SEARCH_PAGE_EG_CLICKSTREAM_NAME,
      clickstreamPageNameDetailed: SearchClickstreamPageNameDetailed[packageType],
    };
  }

  private updateSearchCriteriaFromSearchResults(searchResults: SearchResultsStore, searchCriteria: SearchCriteriaModel) {
    // BFF may provide regionId when coming from SEM or alternate dates
    if (searchResults.searchCriteria) {
      searchCriteria.startDate = searchResults.searchCriteria.startDate;
      searchCriteria.endDate = searchResults.searchCriteria.endDate;
      searchCriteria.regionId = searchResults.searchCriteria.regionId;
      searchCriteria.typeaheadRegionType = searchResults.searchCriteria.typeaheadRegionType;
      searchCriteria.flexibility = searchResults.searchCriteria.flexibility;
      searchCriteria.searchRange = searchResults.searchCriteria.searchRange;
      searchCriteria.flexibleSearchRequirement = searchResults.searchCriteria.flexibleSearchRequirement;
    }
  }

  private previousSearchWasInvalid(searchResults: SearchResultsStore) {
    // Will return true if the previous search contained an invalid location
    return searchResults.isError && searchResults.searchCriteria?.regionId === '';
  }
}
